import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {Button, message} from 'antd';
import {UpOutlined, DownOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import dayjs from 'dayjs';
import {PickupAppointmentFiltersType} from "../graphql/queries/getPickupAppointmentListing";
import { allChannelLiteResponse, GET_ALL_CHANNEL_LITE, ChannelLiteType } from '../../../../graphql/queries/getChannelAll';
import {statusOptions, packageUnitFilterOptions, sourceFilterOptions} from '../types/PickupAppointmentType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { useWarehousePickupZones } from '../../../../hooks/useWarehousePickupZones';
import { GET_PICKUP_ROUTES_BY_WAREHOUSE } from '../../pickup-task/graphql/queries/getPickupRoutes';
import { PickupRouteType } from '../../pickup-task/graphql/types/PickupRouteTypes';
import { pickupFailureReasons, getPickupFailureReasonText } from '../../../../enums/PickupEnum';
import { useVehicleTypes } from '../../../../hooks/useVehicleTypeOptions';
import { LIST_PICKUP_CITIES, ListPickupCitiesResponse } from '../graphql/queries/listPickupCities';

import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateFilterItem from "../../../delivery/components/DateFilterItem";
import { useTranslation } from 'react-i18next';
import {usePickupData} from "../../pickup-task/contexts/PickupDataContext";
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

interface OperateOption {
    value: string;
    label: string;
}

interface PickupAppointmentFilterProps {
    filters: PickupAppointmentFiltersType;
    setFilters: (filters: PickupAppointmentFiltersType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    tempFilters: PickupAppointmentFiltersType;
    setTempFilters: (filters: PickupAppointmentFiltersType) => void;
    disablePickupDate?: boolean;
    disableStatus?: boolean;
    hideButtons?: boolean;
    onMapRouting?: () => void;
    onReportDownload?: () => void;
    visibleFields?: string[];
    disableMapRouting?: boolean;
}


const PickupAppointmentFilter: React.FC<PickupAppointmentFilterProps> = ({
                                                                            filters,
                                                                            setFilters,
                                                                            handleFilter,
                                                                            handleResetFilters,
                                                                            tempFilters,
                                                                            setTempFilters,
                                                                            disablePickupDate = false,
                                                                            disableStatus = false,
                                                                            hideButtons = false,
                                                                            onMapRouting,
                                                                            onReportDownload,
                                                                            visibleFields,
                                                                            disableMapRouting = false,
                                                                        }) => {
    const [channelOptions, setChannelOptions] = useState<OperateOption[]>([]);
    const [collapsed, setCollapsed] = useState(true);
    const {t, i18n} = useTranslation();

    const YES_NO_OPTIONS = [
        { label: t('enum.yesorNoStatus.yes'), value: true },
        { label: t('enum.yesorNoStatus.no'), value: false }
    ];

    const {drivers} = usePickupData();
    const driversOptions = drivers.map(driver => ({
        value: driver.accountId,
        label: driver.driverName
    }));

    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : 0;
    const { pickupZones, loading: pickupZonesLoading } = useWarehousePickupZones(warehouseId);

    const { data: channelData, loading: channelLoading, error: channelError } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);

    const { data: routesData, loading: routesLoading } = useQuery(GET_PICKUP_ROUTES_BY_WAREHOUSE, {
        variables: { warehouseId: warehouseId ? warehouseId : 0 },
        skip: !warehouseId,
    });

    const { vehicleTypes } = useVehicleTypes();

    const { data: citiesData, loading: citiesLoading } = useQuery<ListPickupCitiesResponse>(LIST_PICKUP_CITIES, {
        variables: { warehouseId: warehouseId ? warehouseId : 0 },
        skip: !warehouseId,
    });

    const cityOptions = useMemo(() => {
        return (citiesData?.listPickupCities || []).map(city => ({ label: city, value: city }));
    }, [citiesData?.listPickupCities]);

    useEffect(() => {
        if (channelError) {
            message.error('Failed to load channel list: ' + channelError.message);
            setChannelOptions([]);
            return;
        }
        if (channelData?.getAllChannelsLite) {
            const options = channelData.getAllChannelsLite
                .filter((channel: ChannelLiteType) => channel && channel.id && channel.channelName)
                .map((channel: ChannelLiteType) => ({
                    value: channel.id.toString(),
                    label: channel.channelName
                }));
            setChannelOptions(options);
        } else if (!channelLoading) {
            setChannelOptions([]);
        }
    }, [channelData, channelError, channelLoading]);

    const handleZipcodeListChange = (value: string[] | undefined) => {
        setTempFilters({ ...tempFilters, zipcodeList: value && value.length > 0 ? value : undefined });
    };

    const handleRouteIdListChange = (value: string[] | undefined) => {
        const routeIds = value?.map(v => parseInt(v)).filter(id => !isNaN(id));
        setTempFilters({ ...tempFilters, routeIdList: routeIds && routeIds.length > 0 ? routeIds : undefined });
    };

    const handleSourceListChange = (value: string[] | undefined) => {
        setTempFilters({ ...tempFilters, source: undefined, sourceList: value && value.length > 0 ? value : undefined });
    };

    const handleVehicleTypeIdListChange = (value: string[] | undefined) => {
        const ids = value?.map(v => parseInt(v)).filter(id => !isNaN(id));
        setTempFilters({ ...tempFilters, vehicleTypeId: undefined, vehicleTypeIdList: ids && ids.length > 0 ? ids : undefined });
    };

    const handleStatusListChange = (value: string[] | undefined) => {
        setTempFilters({ ...tempFilters, status: undefined, statusList: value && value.length > 0 ? value : undefined });
    };

    const handleCityListChange = (value: string[] | undefined) => {
        setTempFilters({ ...tempFilters, cityList: value && value.length > 0 ? value : undefined });
    };

    const shouldShowField = (fieldName: string): boolean => {
        if (!visibleFields) return true;
        return visibleFields.includes(fieldName);
    };

    // 4-column label width sets (per PRD layout)
    // col1: 预约单号 | 站点名称 | 站点车辆类型 || 客户 | 连续失败 | 揽收司机
    const col1Labels = [
        t('pickup.appointment.filters.appointmentNumber'),
        t('pickup.appointment.filters.pickupSite'),
        t('pickup.appointment.filters.vehicleModel'),
        t('pickup.appointment.filters.customer'),
        t('pickup.appointment.filters.continuousFailure'),
        t('enum.role.PICKUP_DRIVER'),
        t('pickup.appointment.filters.pickupDate'),
    ];
    // col2: 来源 | 地址 | (空) || 路线 | 运单号 | (空)
    const col2Labels = [
        t('pickup.appointment.filters.source'),
        t('pickup.appointment.columns.address'),
        t('pickup.appointment.filters.route'),
        t('pickup.appointment.filters.trackingNumber'),
    ];
    // col3: 状态 | 城市 | (空) || 邮编 | 提单流水号 | (空)
    const col3Labels = [
        t('pickup.appointment.filters.status'),
        t('pickup.appointment.filters.city'),
        t('pickup.appointment.filters.zipcode'),
        t('pickup.appointment.filters.pickupSerialNo'),
    ];
    // col4: 站点ID | 是否新仓 | (空) || 预期数量单位 | 揽收失败原因 | (空)
    const col4Labels = [
        t('pickup.appointment.filters.stationId'),
        t('pickup.appointment.filters.isNew'),
        t('pickup.appointment.filters.expectedUnit'),
        t('pickup.appointment.filters.failureReason'),
    ];

    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);
    const col4SpanClassName = getSmartLabelClassName(col4Labels);

    const routeOptions = useMemo(() => {
        const routes = routesData?.getPickupRoutesByWarehouseId || [];
        return routes.map((r: PickupRouteType) => ({
            label: r.routeName || r.routeNumber || String(r.id),
            value: String(r.id)
        }));
    }, [routesData?.getPickupRoutesByWarehouseId]);

    // 状态显示值：优先 statusList，否则将旧 status 单值转数组显示
    const statusListDisplayValue = tempFilters.statusList
        ?? (tempFilters.status ? [tempFilters.status] : undefined);

    return (
        <div className="mb-1 pl-0 py-1">
            {/* Step 1 徽标 + 揽收日期 + 地图排线（置顶，disablePickupDate 时整行隐藏） */}
            {!disablePickupDate && shouldShowField('pickupDate') && (
                <div className="mb-2 border border-gray-200 rounded-lg px-2 py-1.5">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-block rounded-full bg-purple-100 text-purple-700 px-3 py-1 text-sm font-medium whitespace-nowrap">
                                {`Step 1 · ${t('pickup.appointment.filters.selectPickupDateStep')}`}
                            </span>
                            <DateFilterItem
                                label=""
                                value={filters.expectedPickupDate ? dayjs(filters.expectedPickupDate) : dayjs()}
                                disabled={disablePickupDate}
                                spanClassName="hidden"
                                onChange={(date) => {
                                    if (disablePickupDate) return;
                                    const selectedDate = date || dayjs();
                                    const newPickupDate = selectedDate.format('YYYY-MM-DD');
                                    setFilters({ ...filters, expectedPickupDate: newPickupDate });
                                    setTempFilters({ ...tempFilters, expectedPickupDate: newPickupDate });
                                }}
                            />
                        </div>
                        {onMapRouting && (
                            <Button
                                type="primary"
                                onClick={onMapRouting}
                                hidden={disableMapRouting}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">{t('pickup.appointment.buttons.mapRouting')}</span>
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* 筛选卡片：边框包裹常显区 + 折叠区 */}
            <div className="border border-gray-200 rounded-lg p-2">
            {/* 常显区：4 列 */}
            <div className="grid grid-cols-4 gap-x-10 gap-y-4 items-start">
                {/* 行1：预约单号 | 来源 | 状态 | 站点ID */}
                {shouldShowField('appointmentNumber') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.appointmentNumber')}
                            options={[]}
                            mode="tags"
                            tokenSeparators={[',', ' ', '/', '\n', '\r']}
                            value={tempFilters.appointmentNumberList || []}
                            onChange={(values: string[]) =>
                                setTempFilters({ ...tempFilters, appointmentNumberList: values.length > 0 ? values : undefined })
                            }
                            spanClassName={col1SpanClassName}
                            placeholder={t('components.input.typeIn') as string}
                            maxTagCount="responsive"
                            notFoundContent={null}
                        />
                    </div>
                )}
                {shouldShowField('source') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.source')}
                            options={sourceFilterOptions}
                            value={tempFilters.sourceList ?? undefined}
                            onChange={(value: string | string[]) => handleSourceListChange(Array.isArray(value) ? value : (value ? [value] : []))}
                            spanClassName={col2SpanClassName}
                            className="w-full"
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </div>
                )}
                {shouldShowField('status') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.status')}
                            options={statusOptions}
                            value={statusListDisplayValue}
                            onChange={(value: string | string[]) => handleStatusListChange(Array.isArray(value) ? value : (value ? [value] : []))}
                            spanClassName={col3SpanClassName}
                            className="w-full"
                            mode="multiple"
                            maxTagCount="responsive"
                            disabled={disableStatus}
                        />
                    </div>
                )}
                {shouldShowField('stationId') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.stationId')}
                            options={[]}
                            mode="tags"
                            tokenSeparators={[',', ' ', '/', '\n', '\r']}
                            value={tempFilters.cooperationWarehouseIdList?.map(String) || []}
                            onChange={(values: string[]) => {
                                const ids = values.map(Number).filter(n => !isNaN(n) && n > 0);
                                setTempFilters({ ...tempFilters, cooperationWarehouseIdList: ids.length > 0 ? ids : undefined });
                            }}
                            spanClassName={col4SpanClassName}
                            placeholder={t('components.input.typeIn') as string}
                            maxTagCount="responsive"
                            notFoundContent={null}
                        />
                    </div>
                )}

                {/* 行2：站点名称 | 地址 | 城市 | 是否新仓 */}
                {shouldShowField('pickupSite') && (
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('pickup.appointment.filters.pickupSite')}
                            value={tempFilters.cooperationWarehouseName || ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value.trim();
                                setTempFilters({ ...tempFilters, cooperationWarehouseName: value || undefined });
                            }}
                            spanClassName={col1SpanClassName}
                        />
                    </div>
                )}
                {shouldShowField('address') && (
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('pickup.appointment.columns.address')}
                            value={tempFilters.address || ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value.trim();
                                setTempFilters({ ...tempFilters, address: value || undefined });
                            }}
                            spanClassName={col2SpanClassName}
                        />
                    </div>
                )}
                {shouldShowField('city') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.city')}
                            options={cityOptions}
                            value={tempFilters.cityList || undefined}
                            onChange={(value: string | string[]) => handleCityListChange(Array.isArray(value) ? value : (value ? [value] : []))}
                            spanClassName={col3SpanClassName}
                            className="w-full"
                            mode="multiple"
                            showSearch
                            loading={citiesLoading}
                            maxTagCount="responsive"
                            disabled={!currentWarehouse || cityOptions.length === 0}
                        />
                    </div>
                )}
                {shouldShowField('isNew') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.isNew')}
                            options={YES_NO_OPTIONS}
                            value={tempFilters.isNew !== undefined ? String(tempFilters.isNew) : undefined}
                            onChange={(value: any) => setTempFilters({
                                ...tempFilters,
                                isNew: value === undefined || value === null || value === '' ? undefined : value === 'true'
                            })}
                            spanClassName={col4SpanClassName}
                            className="w-full"
                        />
                    </div>
                )}

                {/* 行3：站点车辆类型 | 空 | 空 | 空 */}
                {shouldShowField('vehicleTypeId') && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('pickup.appointment.filters.vehicleModel')}
                            options={(vehicleTypes || []).map(vt => ({
                                label: vt.nameEn,
                                value: vt.id.toString()
                            }))}
                            value={tempFilters.vehicleTypeIdList?.map(id => String(id)) || undefined}
                            onChange={(value: string | string[]) => handleVehicleTypeIdListChange(Array.isArray(value) ? value : (value ? [value] : []))}
                            spanClassName={col1SpanClassName}
                            className="w-full"
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </div>
                )}
                <div className="min-w-0" />
                <div className="min-w-0" />
                <div className="min-w-0" />
            </div>

            {/* 折叠区：4 列，默认收起（上方虚线分隔常显/折叠两组） */}
            {!collapsed && (
                <div className="mt-4">
                <div className="border-t border-dashed border-gray-300 mb-4" />
                <div className="grid grid-cols-4 gap-x-10 gap-y-4 items-start">
                    {/* 行1：客户 | 路线 | 邮编 | 预期数量单位 */}
                    {shouldShowField('customer') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.customer')}
                                options={channelOptions}
                                value={tempFilters.channelId?.toString()}
                                onChange={(value: string) => setTempFilters({
                                    ...tempFilters,
                                    channelId: value ? parseInt(value) : undefined
                                })}
                                spanClassName={col1SpanClassName}
                                className="w-full"
                            />
                        </div>
                    )}
                    {shouldShowField('route') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.route')}
                                options={routeOptions}
                                value={tempFilters.routeIdList?.map(id => String(id)) || undefined}
                                onChange={(value: string | string[]) => handleRouteIdListChange(Array.isArray(value) ? value : [])}
                                spanClassName={col2SpanClassName}
                                className="w-full"
                                mode="multiple"
                                showSearch
                                loading={routesLoading}
                                maxTagCount="responsive"
                                placeholder={t('pickup.appointment.placeholders.selectMultipleRoutes') || undefined}
                                disabled={!currentWarehouse || routesLoading || !routesData?.getPickupRoutesByWarehouseId}
                                notFoundContent={
                                    !currentWarehouse ? t('pickup.appointment.pleaseSelectWarehouseFirst') :
                                    !routesData?.getPickupRoutesByWarehouseId ? t('pickup.appointment.noRoutesAvailable') :
                                    t('pickup.appointment.noMatchingRoutes')
                                }
                            />
                        </div>
                    )}
                    {shouldShowField('zipcode') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.zipcode')}
                                options={pickupZones}
                                value={tempFilters.zipcodeList || undefined}
                                onChange={(value: string | string[]) => handleZipcodeListChange(Array.isArray(value) ? value : [])}
                                spanClassName={col3SpanClassName}
                                className="w-full"
                                mode="multiple"
                                showSearch
                                loading={pickupZonesLoading}
                                maxTagCount="responsive"
                                placeholder={t('pickup.appointment.placeholders.selectZipcode') || undefined}
                                disabled={!currentWarehouse || pickupZones.length === 0}
                                notFoundContent={
                                    !currentWarehouse ? t('pickup.appointment.pleaseSelectWarehouseFirst') :
                                    pickupZones.length === 0 ? t('pickup.appointment.noPickupZonesAvailable') :
                                    t('pickup.appointment.noMatchingZipcodes')
                                }
                            />
                        </div>
                    )}
                    {shouldShowField('expectedUnit') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.expectedUnit')}
                                options={packageUnitFilterOptions}
                                value={tempFilters.expectedParcelUnit}
                                onChange={(value: string) => setTempFilters({ ...tempFilters, expectedParcelUnit: value || undefined })}
                                spanClassName={col4SpanClassName}
                                className="w-full"
                            />
                        </div>
                    )}

                    {/* 行2：连续失败 | 运单号 | 提单流水号 | 揽收失败原因 */}
                    {shouldShowField('continuousFailure') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.continuousFailure')}
                                options={YES_NO_OPTIONS}
                                value={tempFilters.continuousFailure !== undefined ? String(tempFilters.continuousFailure) : undefined}
                                onChange={(value: any) => setTempFilters({
                                    ...tempFilters,
                                    continuousFailure: value === undefined || value === null || value === '' ? undefined : value === 'true'
                                })}
                                spanClassName={col1SpanClassName}
                                className="w-full"
                            />
                        </div>
                    )}
                    {shouldShowField('trackingNumber') && (
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('pickup.appointment.filters.trackingNumber')}
                                value={tempFilters.trackingNumber || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value.trim();
                                    setTempFilters({ ...tempFilters, trackingNumber: value || undefined });
                                }}
                                spanClassName={col2SpanClassName}
                            />
                        </div>
                    )}
                    {shouldShowField('pickupSerialNo') && (
                        <div className="min-w-0">
                            <InputFilterItem
                                label={t('pickup.appointment.filters.pickupSerialNo')}
                                value={tempFilters.pickupSerialNo || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value.trim();
                                    setTempFilters({ ...tempFilters, pickupSerialNo: value || undefined });
                                }}
                                spanClassName={col3SpanClassName}
                            />
                        </div>
                    )}
                    {shouldShowField('failureReasonCode') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('pickup.appointment.filters.failureReason')}
                                options={pickupFailureReasons.map((reason) => ({
                                    label: getPickupFailureReasonText(reason.code, i18n.language),
                                    value: reason.code
                                }))}
                                value={tempFilters.failureReasonCode}
                                onChange={(value: string) => setTempFilters({ ...tempFilters, failureReasonCode: value || undefined })}
                                spanClassName={col4SpanClassName}
                                className="w-full"
                            />
                        </div>
                    )}

                    {/* 行3：揽收司机 | 空 | 空 | 空 */}
                    {shouldShowField('driverId') && (
                        <div className="min-w-0">
                            <ParcelFilterStyle
                                label={t('enum.role.PICKUP_DRIVER')}
                                options={driversOptions}
                                value={tempFilters.driverId}
                                onChange={(value: number) => setTempFilters({ ...tempFilters, driverId: value || undefined })}
                                spanClassName={col1SpanClassName}
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="min-w-0" />
                    <div className="min-w-0" />
                    <div className="min-w-0" />
                </div>
                </div>
            )}

            {/* 展开/收起按钮 */}
            <div className="flex justify-center">
                <Button
                    type="link"
                    size="small"
                    onClick={() => setCollapsed(!collapsed)}
                    icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                >
                    {collapsed ? t('common.expand') : t('common.collapse')}
                </Button>
            </div>
            </div>
        </div>
    );
};

export default PickupAppointmentFilter;
